import {
  IconAlertTriangle,
  IconCircleCheck,
  IconInfoCircle,
} from '@tabler/icons-react';
import clx from 'classnames';
import { darken } from 'polished';
import type React from 'react';
import styled from 'styled-components';
import { TbIcon } from '../icon/TbIcon.tsx';

export type ConfigurationCalloutVariant =
  | 'success'
  | 'info'
  | 'warning'
  | 'neutral';

const DEFAULT_ICON: Record<ConfigurationCalloutVariant, React.ReactNode> = {
  success: <TbIcon icon={IconCircleCheck} />,
  info: <TbIcon icon={IconInfoCircle} />,
  warning: <TbIcon icon={IconAlertTriangle} />,
  neutral: <TbIcon icon={IconInfoCircle} />,
};

const CalloutIcon = styled.span`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  font-size: 18px;
  /* Altezza pari a una riga con <Tag> antd: mantiene l'icona centrata sulla
     prima riga sia con testo semplice sia con un Tag (più alto del testo). */
  min-height: 22px;
`;

const CalloutContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const CalloutBody = styled.span`
  min-width: 0;
`;

const CalloutTitle = styled.span`
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
`;

const ConfigurationCalloutWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 6px;
  border: 1px solid transparent;
  font-size: 14px;
  line-height: 1.4;

  &.variant-success {
    background: ${props => props.theme.colorSuccessBg};
    border-color: ${props => props.theme.colorSuccessBorder};
    ${CalloutIcon} {
      color: ${props => props.theme.colorSuccess};
    }
  }

  &.variant-info {
    background: ${props => props.theme.colorBgLayout};
    border-color: ${props => props.theme.colorInfoBorder};
    ${CalloutIcon} {
      color: ${props => props.theme.colorInfo};
    }
  }

  &.variant-warning {
    background: ${props => props.theme.colorWarningBg};
    border-color: ${props => props.theme.colorWarningBorder};
    ${CalloutIcon} {
      color: ${props => props.theme.colorWarning};
    }
  }

  &.variant-neutral {
    background: ${props => props.theme.colorBgLayout};
    border-color: ${props => darken(0.09, props.theme.colorBgLayout)};
    ${CalloutIcon} {
      color: ${props => props.theme.colorTextSecondary};
    }
  }
`;

export interface IConfigurationCalloutProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  /** Tono visivo del callout. Default: `info`. */
  variant?: ConfigurationCalloutVariant;
  /** Icona custom; se omessa usa quella di default della variante. */
  icon?: React.ReactNode;
  /** Titolo opzionale in grassetto (può contenere tag/badge inline). */
  title?: React.ReactNode;
}

/**
 * Callout per testi di aiuto e dettaglio descrittivo da usare dentro le
 * `ConfigurationCard` / `ConfigurationSubCard`. Mostra un'icona colorata in
 * base alla `variant`, un titolo opzionale e il corpo descrittivo.
 */
export function ConfigurationCallout(props: IConfigurationCalloutProps) {
  const {
    variant = 'info',
    icon,
    title,
    children,
    className,
    ...otherProps
  } = props;

  return (
    <ConfigurationCalloutWrapper
      className={clx(className, `variant-${variant}`)}
      {...otherProps}
    >
      <CalloutIcon>{icon ?? DEFAULT_ICON[variant]}</CalloutIcon>
      <CalloutContent>
        {title != null && <CalloutTitle>{title}</CalloutTitle>}
        {children != null && <CalloutBody>{children}</CalloutBody>}
      </CalloutContent>
    </ConfigurationCalloutWrapper>
  );
}
